import type { FetchBaseQueryError } from '@/_state/apis/tanstack-api';

type SerializedError = {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
};

export const getQueryStringValue = (key: string) => {
  const url = new URL(window.location.href);
  return url.searchParams.get(key);
};

export const getFormattedCurrency = (amount: number, withDecimal = false, rounded = false) => {
  if (!amount && amount !== 0) return '';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: withDecimal ? 2 : 0,
  });

  return formatter.format(rounded ? Math.round(amount) : amount);
};

const oPluralize = (num: string | number, word: string, plural = `${word}s`) => {
  const newNumber = Number(num);
  const newWord = [1, -1].includes(newNumber) ? word : plural;
  return `${num} ${newWord}`;
};

export const pluralize = (val: string | number, word: string, plural = `${word}s`) => {
  if (typeof val === 'object') {
    return (num: string | number, nWord: string) => oPluralize(num, nWord, val[nWord]);
  }
  return oPluralize(val, word, plural);
};

export function shuffleArr<T>(array: T[]) {
  const nArr = [...array];
  for (let i = nArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = nArr[i];
    nArr[i] = nArr[j];
    nArr[j] = temp;
  }
  return nArr;
}

export function isEmpty(o = {}) {
  return Object.keys(o)?.length === 0;
}

export function extractContent(s: string) {
  const span = document.createElement('span');
  span.innerHTML = s;
  return span.textContent || span.innerText;
}

export function setZeroError(n: number | string, fixTo = 2, wantDecimal = false) {
  const num = `${parseFloat(n + '').toFixed(fixTo)}`;

  if ((num.split('.').length === 1 || parseInt(num.split('.')[1], 10) === 0) && !wantDecimal) {
    return `${num.split('.')[0]}`;
  }
  return num;
}

export function floatFix(n: number, fixTo = 2, isPercentage = false, wantDecimal = false) {
  if (isNaN(n) || !n) return 0;

  let newN = parseFloat(n + '');
  if (newN < 0 && isPercentage) newN = 0;
  if (newN > 100 && isPercentage) newN = 100;
  return setZeroError(newN, fixTo, wantDecimal);
}

export function getImageSources(str: string) {
  const source = [];
  const div = document.createElement('div');
  div.innerHTML = str;
  const imgNodes = div.getElementsByTagName('img');
  for (let i = 0; i < imgNodes.length; i++) {
    source.push(imgNodes[i].getAttribute('src'));
  }

  return source;
}

export function ordinalIndicator(n: number) {
  const result = {
    number: n,
    finalValue: n + '',
    postFix: 'th',
  };

  if (!isNaN(n)) {
    const r = parseInt(n + '', 10);
    if (r % 10 === 1 && r % 100 !== 11) result.postFix = 'st';
    if (r % 10 === 2 && r % 100 !== 12) result.postFix = 'nd';
    if (r % 10 === 3 && r % 100 !== 13) result.postFix = 'rd';
    result.finalValue = n + result.postFix;
  } else if (typeof n === 'string') {
    const [first] = (n + '').split('');
    result.finalValue = first;
  }
  return result;
}

export const findDuplicates = (arr: unknown[]) =>
  arr.filter((item: unknown, index: number) => arr.indexOf(item) !== index);

export function getApiError(error: FetchBaseQueryError | SerializedError) {
  const { status, data } = error as FetchBaseQueryError;
  const { error_code } = data as unknown as { error_code: number | string; errors: string };

  return { status, error_code };
}

export const handleNumber = (number: string) => {
  return number.replace(/[^\d.]/g, '');
};

export const handleFloat = (number: string) => {
  return safeFloatParse(number) + '';
};

export function safeFloatParse(v: string) {
  const parsed = parseFloat(v);
  return isNaN(parsed) ? 0 : parsed;
}
